import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import boldRoboto from "@/assets/fonts/Roboto/Roboto-Bold.ttf";
import regularRobot from "@/assets/fonts/Roboto/Roboto-Regular.ttf";
import { numberFormater } from "@/utils";
import PropTypes from "prop-types";
const defaultLogo = `${window.location.origin}/images/logo.png`;

Font.register({
  family: "roboto",
  fonts: [{ src: regularRobot }, { src: boldRoboto, fontWeight: "bold" }],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: "60px 50px",
    fontSize: 12,
    fontFamily: "roboto",
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    // justifyContent: "center",
    gap: 20,
  },
  headerBusiness: {
    fontWeight: "bold",
    fontSize: 12,
  },
  title: {
    fontSize: 12,
    marginBottom: 5,
    marginTop: 30,
    paddingBottom: 5,
    borderBottom: "1px solid black",
  },
  pageNumber: {
    textAlign: "right",
    fontSize: 10,
    marginBottom: 20,
  },

  customerFlex: {
    display: "flex",
    flexDirection: "row",
    marginBottom: 5,
  },
  table: {
    display: "flex",
    marginTop: 30,
  },
  tableHeader: {
    textAlign: "center",
    fontWeight: "bold",
    border: "1px solid black",
    padding: "10px 15px",
    backgroundColor: "#e7e6e6",
  },
  tableRow: {
    display: "flex",
    flexDirection: "row",
  },
  tableData: {
    border: "1px solid black",
    padding: "10px 15px",
    width: "100%",
    marginTop: -1,
    marginRight: -1,
    display: "flex",
    flexDirection: "row",
    textAlign: "right",
  },
  footer: {
    marginTop: "auto",
    paddingTop: 30,
    fontSize: 11,
    textAlign: "center",
  },
});

// Create Document Component
const BillingReportPDF = ({ reportValues }) => {
  const logo = reportValues?.firstBusiness?.logo || defaultLogo;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* header */}
        <View fixed>
          <View style={styles.header}>
            <Image src={logo} style={{ width: "30%" }} />
            <View>
              <Text style={styles.headerBusiness}> {reportValues?.business} </Text>
              <Text>This electricity billing report is generated from IVIS.</Text>
            </View>
          </View>
          <Text style={styles.title}>Monthly Electricity Usage Report</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
            fixed
          />
        </View>

        {/* customer info */}
        <View style={styles.customerInfo}>
          <Text style={{ fontWeight: "bold", marginBottom: 10 }}>Customer Information </Text>
          {reportFields.map((field, ind) => (
            <View style={styles.customerFlex} key={ind}>
              <Text style={{ width: "40%" }}> {field?.label} </Text>
              <Text> : </Text>

              <Text style={{ flex: 1, wordBreak: "break-word" }}>{reportValues[field?.value] || ""}</Text>
            </View>
          ))}
        </View>

        {/* electricity uses table */}
        {Boolean(reportValues?.reportType) && (
          <View style={styles.table}>
            <Text style={{ ...styles.tableHeader, marginRight: -1 }}>Electricity Cost Breakdown</Text>

            {/* type 1 */}
            {reportValues?.reportType === "type1" && (
              <>
                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}></Text>
                  <Text style={{ ...styles.tableData, textAlign: "center" }}>Usages</Text>
                  <Text style={{ ...styles.tableData, textAlign: "center", width: 290 }}>Cost (RM)</Text>
                </View>

                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}>Peak Usage This Month</Text>
                  <>
                    <Text style={{ ...styles.tableData, width: "50%" }}>
                      {numberFormater(reportValues?.peakHourElectricityValue || 0)}
                    </Text>
                    <Text style={{ ...styles.tableData, width: "50%" }}>kWh</Text>
                  </>
                  <Text style={{ ...styles.tableData, width: 290 }}>
                    {numberFormater(reportValues?.peakHourElectricityPrice || 0)}
                  </Text>
                </View>

                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}>Off Peak Usage This Month</Text>
                  <>
                    <Text style={{ ...styles.tableData, width: "50%" }}>
                      {numberFormater(reportValues?.offPeakHourElectricityValue || 0)}
                    </Text>
                    <Text style={{ ...styles.tableData, width: "50%" }}>kWh</Text>
                  </>
                  <Text style={{ ...styles.tableData, width: 290 }}>
                    {numberFormater(reportValues?.offPeakHourElectricityPrice || 0)}
                  </Text>
                </View>

                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}>Maximum Demand (kW)</Text>
                  <>
                    <Text style={{ ...styles.tableData, width: "50%" }}>
                      {numberFormater(reportValues?.demandTotalPrice || 0)}
                    </Text>
                    <Text style={{ ...styles.tableData, width: "50%" }}>kW</Text>
                  </>
                  <Text style={{ ...styles.tableData, width: 290 }}>
                    {numberFormater(reportValues?.demandTotalPrice || 0)}
                  </Text>
                </View>
              </>
            )}

            {/* type 2 */}
            {reportValues?.reportType === "type2" && (
              <>
                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}></Text>
                  <Text style={{ ...styles.tableData, textAlign: "center" }}>Usages</Text>
                  <Text style={{ ...styles.tableData, textAlign: "center", width: 290 }}>Cost (RM)</Text>
                </View>

                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}>All Usage This Month</Text>
                  <>
                    <Text style={{ ...styles.tableData, width: "50%" }}>
                      {numberFormater(reportValues?.totalElectricityValue || 0)}
                    </Text>
                    <Text style={{ ...styles.tableData, width: "50%" }}>kWh</Text>
                  </>
                  <Text style={{ ...styles.tableData, width: 290 }}>
                    {numberFormater(reportValues?.totalElectricityPrice || 0)}
                  </Text>
                </View>

                <View style={styles.tableRow}>
                  <Text style={{ ...styles.tableData, width: "150%" }}>Maximum Demand (kW)</Text>
                  <>
                    <Text style={{ ...styles.tableData, width: "50%" }}>
                      {numberFormater(reportValues?.demandTotalValue || 0)}
                    </Text>
                    <Text style={{ ...styles.tableData, width: "50%" }}>kW</Text>
                  </>
                  <Text style={{ ...styles.tableData, width: 290 }}>
                    {numberFormater(reportValues?.demandTotalPrice || 0)}
                  </Text>
                </View>
              </>
            )}
            <View style={{ ...styles.tableRow, marginTop: 40 }}>
              <Text style={{ ...styles.tableData, width: "150%" }}>Total Usage Fee (RM)</Text>
              <Text style={{ ...styles.tableData, width: 190 }}>
                {numberFormater(
                  Number(reportValues?.totalElectricityPrice || 0) + Number(reportValues?.demandTotalPrice || 0),
                )}
              </Text>
            </View>

            <View style={styles.tableRow}>
              <Text style={{ ...styles.tableData, width: "150%" }}>Service Tax ({reportValues?.type || 0})%</Text>
              <Text style={{ ...styles.tableData, width: 190 }}>{numberFormater(reportValues?.serviceTax || 0)}</Text>
            </View>

            <View style={{ ...styles.tableRow, fontWeight: "bold" }}>
              <Text style={{ ...styles.tableData, width: "150%" }}>Total Charge (RM)</Text>
              <Text style={{ ...styles.tableData, width: 190 }}>{numberFormater(reportValues?.totalCharge || 0)}</Text>
            </View>
          </View>
        )}

        {/* Footer section */}
        <View fixed style={styles.footer}>
          <Text>If there are any discrepancies or disputes, kindly contact your building management.</Text>
          <Text style={{ fontWeight: "bold", marginTop: 20 }}>
            THIS IS AN ELECTRONICALLY GENERATED REPORT FROM iViS
          </Text>
        </View>
      </Page>
    </Document>
  );
};

BillingReportPDF.propTypes = {
  reportValues: PropTypes.object.isRequired,
};

export default BillingReportPDF;

const reportFields = [
  { label: "Report Generation Date", value: "reportGenerationDate" },
  { label: "Monthly Cycle Selected", value: "monthlyCycleSelected" },
  { label: "Site Location", value: "site" },
  { label: "Device Name", value: "device" },
  { label: "Device ID", value: "device" },
  { label: "Installed Area", value: "installedArea" },
  { label: "Electricity Tariff Type", value: "tariffName" },
];
